import { useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import {
  ProductSelectionsFetchRequest,
  ProductSelectionsSubmitRequest,
  Result,
  ResultStates,
  useInvalidateInvestmentDetails,
  useInvalidateProposalInformation
} from 'bff';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { clientService } from 'stores/client-store/client-store';
import { useInvalidateThirdParties } from '../applications/third-parties/ThirdPartiesApi';
import { useInvalidatePaymentDetails } from '../applications/payment-details/PaymentDetailsApi';

export const getCurrentProductsKey = 'get-current-products';

export const useInvalidateCurrentProducts = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [getCurrentProductsKey],
      type: 'all'
    });
};

export const getCurrentProducts = (productSelectionRequest: ProductSelectionsFetchRequest) => {
  const { productSelectionsApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: [getCurrentProductsKey, productSelectionRequest],
    queryFn: () => productSelectionsApi.productSelectionsFetch(productSelectionRequest),
    refetchOnWindowFocus: false,
    select: (response) => response.data!
  });
};

export const submitProductSelectionMutateKey = ['submit-product-selection'];

export const useSubmitProductSelection = (
  options: UseDisplayErrorMutationOptions<Result, ProductSelectionsSubmitRequest>
) => {
  const { productSelectionsApi } = clientService.getApiClient();
  const queryClient = useQueryClient();
  const invalidateFormState = useInvalidateProposalInformation();
  const invalidateCurrentProducts = useInvalidateCurrentProducts();
  const invalidateInvestmentDetails = useInvalidateInvestmentDetails();
  const invalidateThirdParties = useInvalidateThirdParties();
  const invalidatePayments = useInvalidatePaymentDetails();

  return useMutation({
    ...options,
    mutationKey: submitProductSelectionMutateKey,
    mutationFn: (productSelectionRequest: ProductSelectionsSubmitRequest) =>
      productSelectionsApi.productSelectionsSubmit(productSelectionRequest),
    onSuccess: async (data, variables, context) => {
      if (data.resultState === ResultStates.DisplayError) {
        options.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          queryClient.invalidateQueries({
            queryKey: [getCurrentProductsKey]
          }),
          invalidateInvestmentDetails(),
          invalidateFormState(),
          invalidateCurrentProducts(),
          invalidateThirdParties(),
          invalidatePayments()
        ]);
        options.onSuccess?.(data, variables, context);
      }
    }
  });
};
